import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import authService from '@/services/authService'
import paymentService from '@/services/paymentService'
import logger from '@/utils/logger'
import { useSessionActivity } from '@/composables/useSessionActivity'
import { formatError } from '@/composables/useErrorHandler'

/**
 * Auth Store for Olvidadme - Secure Cookie-Based Session Management
 *
 * Implementing Secure Cookie-Based Session Management Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Following ADR-084: Secure Session Management and Persistence Refactor
 * Connects with enterprise-ready backend auth system (8 endpoints + session validation)
 *
 * Security Features:
 * - NO token storage in memory/localStorage (httpOnly cookies only)
 * - Session validation via /api/auth/session endpoint
 * - Client-side inactivity detection with auto-logout
 * - Session fingerprinting validation
 * - XSS protection (zero token exposure to JavaScript)
 * - User profile state with GDPR compliance
 * - Spanish DNI/NIE support
 * - Foral jurisdiction handling
 * - GDPR audit logging
 */
export const useAuthStore = defineStore('auth', () => {
  // ==========================================
  // STATE - Reactive references
  // ==========================================

  const user = ref(null)
  const isLoading = ref(false)
  const error = ref(null)
  const errorCode = ref(null)
  const validationErrors = ref([])

  // Login attempt tracking for rate limiting compliance
  const loginAttempts = ref(0)
  const lastLoginAttempt = ref(null)

  // ✅ NEW: Session activity tracking using composable (Task 3.1)
  const sessionActivity = useSessionActivity()

  // ✅ NEW: Router Guards Optimization State (Task 2.3)
  const isInitializing = ref(false)
  const initializePromise = ref(null)
  const hasInitialized = ref(false)

  // ==========================================
  // GETTERS - Computed properties
  // ==========================================
  
  const isAuthenticated = computed(() => {
    // ✅ NEW: Authentication based only on user presence
    // Tokens are managed via httpOnly cookies, not accessible to JS
    return !!user.value
  })
  
  const isAdmin = computed(() => {
    return user.value?.role === 'admin'
  })
  
  const isLawyer = computed(() => {
    return user.value?.role === 'lawyer'
  })
  
  const hasForalRights = computed(() => {
    return user.value?.address?.hasForalRights || false
  })
  
  const foralTerritory = computed(() => {
    return user.value?.address?.foralTerritory || null
  })
  
  const canAccessPremiumFeatures = computed(() => {
    // Updated for new service model - testamento has premium-like features
    return user.value?.subscriptionTier === 'testamento' || isAdmin.value
  })
  
  const userDisplayName = computed(() => {
    if (!user.value) return null
    return user.value.name || user.value.email
  })

  // Rate limiting check
  const canAttemptLogin = computed(() => {
    if (loginAttempts.value < 5) return true
    if (!lastLoginAttempt.value) return true

    const timeSinceLastAttempt = Date.now() - lastLoginAttempt.value
    const thirtyMinutes = 30 * 60 * 1000

    return timeSinceLastAttempt > thirtyMinutes
  })

  // Unified tier getter for consistency across the application
  // Implementing Tier-Aware Detection Pattern from PATTERNS_FROM_BASKET_BASE.md
  // Following ADR-096: Dashboard Tier-Aware Architecture
  const userTier = computed(() => {
    // Use subscriptionTier from backend (only valid field in User model)
    // Updated fallback to 'olvido' for new service model (Sprint 03)
    const tier = user.value?.subscriptionTier || 'olvido'

    logger.debug('User tier detection', {
      userId: user.value?._id,
      subscriptionTier: user.value?.subscriptionTier,
      detectedTier: tier
    })

    return tier
  })

  // ==========================================
  // ACTIONS - Authentication operations
  // ==========================================

  /**
   * Login user with email/password or DNI/password
   * Applying authentication patterns from Basket Base
   * 
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - Email or DNI
   * @param {string} credentials.password - Password
   * @param {boolean} credentials.rememberMe - Extended session
   */
  const login = async (credentials) => {
    if (!canAttemptLogin.value) {
      throw new Error('Demasiados intentos de login. Intente de nuevo en 30 minutos.')
    }

    isLoading.value = true
    error.value = null
    
    try {
      // Track login attempt
      loginAttempts.value++
      lastLoginAttempt.value = Date.now()
      
      const response = await authService.login(credentials)
      
      // Store user data (tokens handled via httpOnly cookies)
      user.value = response.user

      // ✅ NEW: Start activity tracking using composable (Task 3.1)
      startActivityTracking()
      
      // Reset login attempts on success
      loginAttempts.value = 0
      lastLoginAttempt.value = null
      
      logger.info('User logged in successfully', {
        userId: user.value._id,
        hasForalRights: hasForalRights.value,
        subscriptionTier: user.value.subscriptionTier
      })
      
      return response
      
    } catch (err) {
      const formatted = formatError(err, 'Error al iniciar sesión')
      error.value = formatted.message
      errorCode.value = formatted.code
      validationErrors.value = formatted.details

      logger.warn('Login attempt failed', {
        email: credentials.email,
        attempts: loginAttempts.value,
        error: error.value,
        errorCode: errorCode.value
      })

      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Register new user with GDPR compliance
   * Handles Spanish DNI/NIE validation and foral jurisdictions
   * 
   * @param {Object} userData - Registration data
   */
  const register = async (userData) => {
    isLoading.value = true
    error.value = null
    validationErrors.value = []
    
    try {
      const response = await authService.register(userData)
      
      // Map response structure correctly from backend
      // Backend returns: { success: true, data: { user: {...} } }
      user.value = response.data?.user || response.user
      
      // No auto-login on registration - user needs email verification
      
      logger.info('User registered successfully', {
        userId: user.value?.id,
        email: user.value?.email,
        hasForalRights: hasForalRights.value,
        requiresVerification: response.data?.requiresVerification
      })
      
      return response
      
    } catch (err) {
      const formatted = formatError(err, 'Error al crear cuenta')
      error.value = formatted.message
      errorCode.value = formatted.code
      validationErrors.value = formatted.details

      logger.warn('Registration failed', {
        email: userData.email,
        error: error.value,
        errorCode: errorCode.value,
        validationErrors: validationErrors.value
      })

      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Register user with testing endpoint (staging/development only)
   * Bypasses payment verification and allows direct tier assignment
   * Only works when SKIP_PAYMENT_VERIFICATION=true on backend
   *
   * @param {Object} userData - Registration data (includes optional planTier)
   * @returns {Promise<Object>} Registration response with tokens and user data
   */
  const registerTesting = async (userData) => {
    isLoading.value = true
    error.value = null
    validationErrors.value = []

    try {
      logger.info('Testing registration initiated', {
        email: userData.email,
        planTier: userData.planTier || 'default',
        environment: import.meta.env.MODE
      })

      // Call testing endpoint with plan tier included
      const response = await authService.registerTesting(userData)

      // Store user data from response
      user.value = response.data?.user || response.user

      // Start activity tracking for immediate login
      startActivityTracking()

      logger.info('Testing registration successful with immediate login', {
        userId: user.value?._id,
        email: user.value?.email,
        subscriptionTier: user.value?.subscriptionTier,
        hasForalRights: hasForalRights.value
      })

      return response

    } catch (err) {
      const formatted = formatError(err, 'Error al crear cuenta de prueba')
      error.value = formatted.message
      errorCode.value = formatted.code
      validationErrors.value = formatted.details

      logger.warn('Testing registration failed', {
        email: userData.email,
        error: error.value,
        errorCode: errorCode.value,
        validationErrors: validationErrors.value
      })

      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Register user with paid plan (post-payment registration)
   * Called after successful Stripe payment to complete registration
   *
   * @param {Object} userData - Registration data
   * @param {string} planTier - Selected plan tier
   * @param {string} stripeSessionId - Stripe session ID for payment verification
   */
  const registerWithPlan = async (userData, planTier, stripeSessionId) => {
    isLoading.value = true
    error.value = null
    validationErrors.value = []
    
    try {
      // Prepare registration data for backend
      const registrationData = {
        ...userData,
        stripeSessionId,
        planTier,
        source: 'web'
      }

      // Complete registration after payment using paymentService
      const response = await paymentService.completeRegistrationAfterPayment(stripeSessionId, planTier, userData)
      
      // Check if the operation was successful
      if (!response.success) {
        throw new Error(response.error || 'Failed to complete registration')
      }
      
      // Extract data from successful response
      const responseData = response.data
      
      // Store user data (tokens handled via httpOnly cookies)
      user.value = responseData.user

      // ✅ NEW: Start activity tracking using composable (Task 3.1)
      startActivityTracking()
      
      logger.info('User registered with plan successfully', {
        userId: user.value._id,
        email: user.value.email,
        planTier: responseData.planInfo?.tier,
        subscriptionTier: user.value.subscriptionTier,
        paymentStatus: user.value.paymentStatus,
        requiresEmailVerification: responseData.requiresEmailVerification
      })
      
      return responseData
      
    } catch (err) {
      const formatted = formatError(err, 'Error al completar el registro con plan')
      error.value = formatted.message
      errorCode.value = formatted.code
      validationErrors.value = formatted.details

      logger.error('Registration with plan failed', {
        email: userData.email,
        planTier,
        stripeSessionId,
        error: error.value,
        errorCode: errorCode.value,
        validationErrors: validationErrors.value
      })

      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Logout user and clean session
   * Applies security patterns from Basket Base
   */
  const logout = async () => {
    isLoading.value = true
    
    try {
      // Call backend logout (invalidates httpOnly cookies)
      // ✅ NEW: Always call logout - tokens are in cookies, not memory
      await authService.logout()
      
      logger.info('User logged out', {
        userId: user.value?.id
      })
      
    } catch (err) {
      // Log error but don't throw - we want to clear local state anyway
      logger.warn('Logout request failed', {
        error: err.message
      })
    } finally {
      // Always clear local state and stop activity tracking
      clearAuthState()
      // ✅ NEW: Stop activity tracking using composable (Task 3.1)
      stopActivityTracking()
      isLoading.value = false
    }
  }

  /**
   * Refresh access token using refresh token
   * ✅ NEW: Tokens handled via httpOnly cookies, no memory storage
   * Automatic token refresh handled by axios interceptors
   */
  const refreshToken = async () => {
    try {
      const response = await authService.refreshToken()

      // Tokens now handled via httpOnly cookies automatically

      // Update user data if provided
      if (response.user) {
        user.value = response.user
      }

      return response

    } catch (err) {
      // Refresh failed - logout user
      await logout()
      throw err
    }
  }

  /**
   * Get current user profile
   * Updates user state with latest data
   */
  const fetchProfile = async () => {
    if (!isAuthenticated.value) return null
    
    try {
      const response = await authService.getProfile()
      user.value = response.data?.user

      return response.data?.user
      
    } catch (err) {
      // If profile fetch fails with auth error, logout
      if (err.response?.status === 401) {
        await logout()
      }
      throw err
    }
  }

  /**
   * Update user profile
   * 
   * @param {Object} profileData - Updated profile data
   */
  const updateProfile = async (profileData) => {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await authService.updateProfile(profileData)
      user.value = response.data?.user

      logger.info('Profile updated', {
        userId: user.value?._id
      })
      
      return response
      
    } catch (err) {
      const formatted = formatError(err, 'Error al actualizar perfil')
      error.value = formatted.message
      errorCode.value = formatted.code
      validationErrors.value = formatted.details
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Request password reset
   * 
   * @param {string} email - User email
   */
  const forgotPassword = async (email) => {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await authService.forgotPassword(email)
      
      logger.info('Password reset requested', {
        email
      })
      
      return response
      
    } catch (err) {
      const formatted = formatError(err, 'Error al solicitar restablecimiento')
      error.value = formatted.message
      errorCode.value = formatted.code
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Reset password with token
   *
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   */
  const resetPassword = async (token, newPassword) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.resetPassword(token, newPassword)

      logger.info('Password reset successful')

      return response

    } catch (err) {
      const formatted = formatError(err, 'Error al restablecer contraseña')
      error.value = formatted.message
      errorCode.value = formatted.code
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Change user password (PROF-005)
   *
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   */
  const changePassword = async (currentPassword, newPassword) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.changePassword(currentPassword, newPassword)

      logger.info('Password changed successfully')

      return response

    } catch (err) {
      const formatted = formatError(err, 'Error al cambiar contraseña')
      error.value = formatted.message
      errorCode.value = formatted.code
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Verify email with token
   *
   * @param {string} token - Verification token
   */
  const verifyEmail = async (token) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.verifyEmail(token)

      // Update user state if logged in
      if (isAuthenticated.value) {
        user.value.emailVerified = true
      }

      logger.info('Email verified successfully')

      return response

    } catch (err) {
      const formatted = formatError(err, 'Error al verificar email')
      error.value = formatted.message
      errorCode.value = formatted.code
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Request email change with verification (PROF-004)
   *
   * @param {string} newEmail - New email address
   */
  const requestEmailChange = async (newEmail) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.requestEmailChange(newEmail)

      // Update user state with pending email
      if (user.value) {
        user.value.pendingEmail = response.data?.pendingEmail
      }

      logger.info('Email change requested', {
        userId: user.value?._id,
        pendingEmail: response.data?.pendingEmail
      })

      return response

    } catch (err) {
      const formatted = formatError(err, 'Error al solicitar cambio de email')
      error.value = formatted.message
      errorCode.value = formatted.code
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Cancel pending email change (PROF-004)
   */
  const cancelEmailChange = async () => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.cancelEmailChange()

      // Clear pending email from user state
      if (user.value) {
        user.value.pendingEmail = undefined
      }

      logger.info('Email change cancelled', {
        userId: user.value?._id
      })

      return response

    } catch (err) {
      const formatted = formatError(err, 'Error al cancelar cambio de email')
      error.value = formatted.message
      errorCode.value = formatted.code
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Resend email change verification (PROF-004)
   */
  const resendEmailChangeVerification = async () => {
    isLoading.value = true
    error.value = null

    try {
      const response = await authService.resendEmailChangeVerification()

      logger.info('Email change verification resent', {
        userId: user.value?._id
      })

      return response

    } catch (err) {
      const formatted = formatError(err, 'Error al reenviar verificacion')
      error.value = formatted.message
      errorCode.value = formatted.code
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // ==========================================
  // CLIENT-SIDE ACTIVITY TRACKING - Using Composable (Task 3.1)
  // ==========================================

  /**
   * Start tracking user activity for automatic logout
   * ✅ NEW: Now uses useSessionActivity composable (Task 3.1)
   * Implements client-side inactivity detection per ADR-024
   */
  const startActivityTracking = () => {
    if (!isAuthenticated.value) return

    // Configure activity tracking with user context
    const config = {
      inactivityPeriod: 30 * 60 * 1000, // 30 minutes
      enableLogging: true,
      userId: user.value?._id
    }

    // Callback for inactivity timeout - triggers logout
    const onInactivityCallback = async (reason) => {
      logger.warn('User session expired due to inactivity (composable)', {
        userId: user.value?._id,
        reason,
        sessionActivity: sessionActivity.getActivityStatus()
      })

      // Automatic logout due to inactivity
      await logout()
    }

    // Start activity tracking using composable
    sessionActivity.startActivityTracking(onInactivityCallback, config)

    logger.debug('Activity tracking started (composable)', {
      userId: user.value?._id,
      config: {
        timeoutMinutes: Math.round(config.inactivityPeriod / 1000 / 60),
        eventsCount: sessionActivity.defaultConfig.events.length
      }
    })
  }

  /**
   * Stop activity tracking and cleanup
   * ✅ NEW: Now uses useSessionActivity composable (Task 3.1)
   * Called during logout
   */
  const stopActivityTracking = () => {
    const config = {
      enableLogging: true,
      userId: user.value?._id
    }

    // Stop activity tracking using composable
    sessionActivity.stopActivityTracking(config)

    logger.debug('Activity tracking stopped (composable)', {
      userId: user.value?._id
    })
  }

  // ==========================================
  // UTILITY FUNCTIONS
  // ==========================================

  /**
   * Clear all authentication state
   * Used during logout and on auth errors
   */
  const clearAuthState = () => {
    user.value = null
    error.value = null
    // Don't reset login attempts - they should persist

    // ✅ NEW: Reset initialization state (Task 2.3)
    hasInitialized.value = false
    isInitializing.value = false
    initializePromise.value = null
  }

  /**
   * Clear error state
   */
  const clearError = () => {
    error.value = null
    validationErrors.value = []
  }

  /**
   * Set error state manually
   * Used for errors outside of action methods (e.g., pre-validation in components)
   * @param {string} message - Error message to display
   * @param {string|null} code - Error code for programmatic handling
   * @param {Array} details - Validation error details
   */
  const setError = (message, code = null, details = []) => {
    error.value = message
    errorCode.value = code
    validationErrors.value = details
  }

  /**
   * Initialize auth state from existing session - OPTIMIZED (Task 2.3)
   * ✅ Enhanced: Implements caching to prevent redundant calls
   * ✅ Enhanced: Loading state management for better UX
   * ✅ Enhanced: Promise reuse to handle concurrent calls
   * Called on app startup and router guards
   */
  const initializeAuth = async () => {
    // ✅ OPTIMIZATION: Return cached promise if initialization in progress
    if (initializePromise.value) {
      logger.debug('Returning cached initialization promise')
      return initializePromise.value
    }

    // ✅ OPTIMIZATION: Skip if already initialized successfully
    if (hasInitialized.value && user.value) {
      logger.debug('Auth already initialized, skipping')
      return Promise.resolve()
    }

    // ✅ OPTIMIZATION: Set loading state and create promise
    isInitializing.value = true

    const promise = (async () => {
      try {
        logger.debug('Starting auth initialization')

        // Use new session validation endpoint
        const response = await authService.checkSession()

        if (response.authenticated && response.user) {
          user.value = response.user

          // ✅ NEW: Start activity tracking using composable (Task 3.1)
          startActivityTracking()

          logger.info('Session restored from cookies', {
            userId: user.value._id,
            sessionValid: response.sessionValid
          })
        } else if (response.needsRefresh) {
          // Session needs refresh, let interceptors handle it
          logger.info('Session needs refresh, will be handled automatically')
        } else {
          // No valid session
          clearAuthState()
        }

        // ✅ OPTIMIZATION: Mark as successfully initialized
        hasInitialized.value = true

      } catch (err) {
        // Session validation failed - clear state
        logger.warn('Session initialization failed', {
          error: err.message
        })
        clearAuthState()

        // ✅ OPTIMIZATION: Don't mark as initialized on error
        hasInitialized.value = false
      } finally {
        // ✅ OPTIMIZATION: Always clear loading state and promise
        isInitializing.value = false
        initializePromise.value = null
      }
    })()

    // ✅ OPTIMIZATION: Cache the promise to prevent concurrent calls
    initializePromise.value = promise

    return promise
  }

  /**
   * Check if user has permission for resource
   * 
   * @param {string} resource - Resource type ('case', 'document', etc.)
   * @param {string} action - Action type ('read', 'write', 'delete')
   * @param {Object} resourceData - Resource data for ownership check
   */
  const hasPermission = (resource, action, resourceData = null) => {
    if (!isAuthenticated.value) return false
    
    // Admin has all permissions
    if (isAdmin.value) return true
    
    // Lawyers have read access to assigned cases
    if (isLawyer.value && resource === 'case' && action === 'read') {
      return resourceData?.assignedLawyer === user.value._id
    }
    
    // Users have full access to their own resources
    if (resourceData?.userId === user.value._id) return true
    
    // Default: no permission
    return false
  }

  // ==========================================
  // RETURN STORE INTERFACE
  // ==========================================

  return {
    // State
    user: computed(() => user.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    errorCode: computed(() => errorCode.value),
    validationErrors: computed(() => validationErrors.value),
    loginAttempts: computed(() => loginAttempts.value),

    // ✅ NEW: Router Guards Optimization State (Task 2.3)
    isInitializing: computed(() => isInitializing.value),
    hasInitialized: computed(() => hasInitialized.value),
    
    // Getters
    isAuthenticated,
    isAdmin,
    isLawyer,
    hasForalRights,
    foralTerritory,
    canAccessPremiumFeatures,
    userDisplayName,
    canAttemptLogin,
    userTier,
    
    // Actions
    login,
    register,
    registerWithPlan,
    registerTesting,
    logout,
    refreshToken,
    fetchProfile,
    updateProfile,
    forgotPassword,
    resetPassword,
    changePassword,
    verifyEmail,

    // Email change (PROF-004)
    requestEmailChange,
    cancelEmailChange,
    resendEmailChangeVerification,

    // Utilities
    clearError,
    setError,
    initializeAuth,
    hasPermission
  }
})